interface Dimensions {
  height: number;
  width: number;
}

type OutputType = "base64" | "blob" | "file";
type CompressFormat = "jpeg" | "png" | "webp" | "jpg";

export class Resizer {
  static changeHeightWidth(
    height: number,
    maxHeight: number,
    width: number,
    maxWidth: number,
    minWidth?: number,
    minHeight?: number
  ): Dimensions {
    if (width > maxWidth) {
      height = Math.round((height * maxWidth) / width);
      width = maxWidth;
    }
    if (height > maxHeight) {
      width = Math.round((width * maxHeight) / height);
      height = maxHeight;
    }
    if (minWidth && width < minWidth) {
      height = Math.round((height * minWidth) / width);
      width = minWidth;
    }
    if (minHeight && height < minHeight) {
      width = Math.round((width * minHeight) / height);
      height = minHeight;
    }
    return { height, width };
  }

  static resizeAndRotateImage(
    image: HTMLImageElement,
    maxWidth: number,
    maxHeight: number,
    minWidth?: number,
    minHeight?: number,
    compressFormat: CompressFormat = "jpeg",
    quality: number = 100,
    rotation: number = 0
  ): string {
    const qualityDecimal = quality / 100;
    const canvas = document.createElement("canvas");

    let width = image.width;
    let height = image.height;

    const newHeightWidth = this.changeHeightWidth(height, maxHeight, width, maxWidth, minWidth, minHeight);
    if (rotation && (rotation === 90 || rotation === 270)) {
      canvas.width = newHeightWidth.height;
      canvas.height = newHeightWidth.width;
    } else {
      canvas.width = newHeightWidth.width;
      canvas.height = newHeightWidth.height;
    }

    width = newHeightWidth.width;
    height = newHeightWidth.height;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Failed to get canvas context");
    }
    ctx.fillStyle = "rgba(0, 0, 0, 0)";
    ctx.fillRect(0, 0, width, height);

    if (ctx.imageSmoothingEnabled && ctx.imageSmoothingQuality) {
      ctx.imageSmoothingQuality = "high";
    }

    if (rotation) {
      ctx.rotate((rotation * Math.PI) / 180);
      if (rotation === 90) {
        ctx.translate(0, -canvas.width);
      } else if (rotation === 180) {
        ctx.translate(-canvas.width, -canvas.height);
      } else if (rotation === 270) {
        ctx.translate(-canvas.height, 0);
      } else if (rotation === 0 || rotation === 360) {
        ctx.translate(0, 0);
      }
    }
    ctx.drawImage(image, 0, 0, width, height);

    return canvas.toDataURL(`image/${compressFormat}`, qualityDecimal);
  }

  static b64toByteArrays(b64Data: string): Uint8Array[] {
    const sliceSize = 512;

    const byteCharacters = atob(b64Data.toString().replace(/^data:image\/(png|jpeg|jpg|webp);base64,/, ""));
    const byteArrays: Uint8Array[] = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);

      byteArrays.push(byteArray);
    }
    return byteArrays;
  }

  static b64toBlob(b64Data: string, contentType?: string): Blob {
    const byteArrays = this.b64toByteArrays(b64Data);
    const blob = new Blob(byteArrays as BlobPart[], { type: contentType });
    return blob;
  }

  static b64toFile(b64Data: string, fileName: string, contentType?: string): File {
    const byteArrays = this.b64toByteArrays(b64Data);
    const file = new File(byteArrays as BlobPart[], fileName, { type: contentType });
    return file;
  }

  static createResizedImage(
    file: File | Blob,
    maxWidth: number,
    maxHeight: number,
    compressFormat: CompressFormat,
    quality: number,
    rotation: number,
    outputType: OutputType = "base64",
    minWidth?: number | null,
    minHeight?: number | null
  ): Promise<string | Blob | File> {
    return new Promise((resolve, reject) => {
      if (!file) {
        reject(new Error("File Not Found!"));
        return;
      }

      if (file.type && !file.type.includes("image")) {
        reject(new Error("File Is NOT Image!"));
        return;
      }

      const reader = new FileReader();
      reader.readAsDataURL(file as Blob);

      reader.onload = (): void => {
        const image = new Image();
        image.src = (reader.result as string) || "";
        image.onload = (): void => {
          const resizedDataUrl = Resizer.resizeAndRotateImage(
            image,
            maxWidth,
            maxHeight,
            minWidth ?? undefined,
            minHeight ?? undefined,
            compressFormat,
            quality,
            rotation
          );
          const contentType = `image/${compressFormat}`;
          switch (outputType) {
            case "blob": {
              const blob = Resizer.b64toBlob(resizedDataUrl, contentType);
              resolve(blob);
              break;
            }
            case "base64": {
              resolve(resizedDataUrl);
              break;
            }
            case "file": {
              const fileName = file instanceof File ? file.name : `image.${compressFormat}`;
              const fileNameWithoutFormat = fileName.toString().replace(/(png|jpeg|jpg|webp)$/i, "");
              const newFileName = fileNameWithoutFormat.concat(compressFormat.toString());
              const newFile = Resizer.b64toFile(resizedDataUrl, newFileName, contentType);
              resolve(newFile);
              break;
            }
            default: {
              resolve(resizedDataUrl);
            }
          }
        };
        image.onerror = (): void => {
          reject(new Error("Failed to load image"));
        };
      };

      reader.onerror = (): void => {
        reject(new Error("File reading error"));
      };
    });
  }
}
export default {
  imageFileResizer: (
    file: File | Blob,
    maxWidth: number,
    maxHeight: number,
    compressFormat: CompressFormat,
    quality: number,
    rotation: number,
    outputType?: OutputType,
    minWidth?: number | null,
    minHeight?: number | null
  ): Promise<string | Blob | File> => {
    return Resizer.createResizedImage(
      file,
      maxWidth,
      maxHeight,
      compressFormat,
      quality,
      rotation,
      outputType,
      minWidth,
      minHeight
    );
  },
};
